import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarClock, Library, ClipboardCheck, Plus, Sparkles, Loader2, Trash2, Video, X, CheckCircle2, Gift } from "lucide-react";
import { InterviewList, InterviewDetail } from "@/components/interviews/InterviewDetail";
import { motion, AnimatePresence } from "framer-motion";
import { useMotionPresets, DUR, EASE } from "@/lib/motion";

const REC = { advance: "Advance", hold: "Hold", decline: "Decline" };
const REC_COLOR = { advance: "text-emerald-400", hold: "text-amber-400", decline: "text-rose-400" };

export default function Interviews() {
  const [tab, setTab] = useState("upcoming");
  const [interviews, setInterviews] = useState([]);
  const [kits, setKits] = useState([]);
  const [cands, setCands] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [debriefFor, setDebriefFor] = useState(null);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [offerFor, setOfferFor] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/interviews"), api.get("/interview-kits"),
      api.get("/interview/candidates"), api.get("/jobs"),
    ]).then(([i, k, c, j]) => { setInterviews(i.data); setKits(k.data); setCands(c.data); setJobs(j.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const [selId, setSelId] = useState(null);
  const M = useMotionPresets();
  if (loading) return <LoadingState label="Loading interview workspace…" />;

  const over = (i) => Date.parse(i.when) + (i.duration_min || 60) * 6e4 < Date.now();
  const byTime = (a, b) => Date.parse(a.when) - Date.parse(b.when);
  const upcoming = interviews.filter((i) => i.status === "scheduled" && !over(i)).sort(byTime);
  const due = interviews.filter((i) => i.status === "scheduled" && over(i)).sort(byTime);
  const done = interviews.filter((i) => i.status === "completed").sort((a, b) => byTime(b, a));
  const lists = { upcoming, due, debrief: done };
  const list = lists[tab] || [];
  const current = list.find((i) => i.id === selId) || list[0];

  const saveKit = async (iid) => {
    try {
      await api.post(`/interviews/${iid}/kit-from-debrief`);
      toast.success("Saved as a reusable kit — find it in Question kits");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save kit"); }
  };

  const TABS2 = [["upcoming", `Upcoming · ${upcoming.length}`], ["due", `Due · ${due.length}`], ["debrief", `Debriefs · ${done.length}`], ["kits", "Kits"]];
  const EMPTY = { upcoming: ["No interviews scheduled", "Schedule one from here, or move a strong applicant to Interview."], due: ["No feedback due", "Scorecards for finished interviews appear here until they're submitted."], debrief: ["No debriefs yet", "Completed scorecards and hiring recommendations appear here."] };

  return (
    <div className="recruiter-page" data-testid="interviews-page">
      <div className="grid lg:grid-cols-[380px_minmax(0,1fr)] gap-8 items-start">
        <aside className="space-y-4 lg:sticky lg:top-[84px]">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-3xl font-display font-semibold tracking-tight">Interviews</h1>
            <Button onClick={() => setShowSchedule(true)} data-testid="open-schedule" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Schedule</Button>
          </div>
          <div className="grid grid-cols-4 p-1 rounded-lg bg-foreground/[0.04] border border-border" role="tablist" data-testid="interview-tabs">
            {TABS2.map(([key, label]) => (
              <button key={key} role="tab" aria-selected={tab === key} onClick={() => { setTab(key); setSelId(null); }} data-active={tab === key} data-testid={`itab-${key}`}
                className={`px-1.5 py-2 rounded-md text-[12px] sm:text-[13px] truncate transition-colors ${tab === key ? "bg-foreground/[0.09] font-semibold" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
            ))}
          </div>
          {tab !== "kits" && (list.length ? <InterviewList items={list} selected={current?.id} onSelect={setSelId} testId={tab === "debrief" ? "debrief-list" : "upcoming-list"} />
            : <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`${tab}-empty-note`}>{EMPTY[tab][0]}</p>)}
        </aside>
        <main className="min-w-0">
          <AnimatePresence mode="wait">
            <motion.div key={`${tab}-${current?.id || "none"}`} initial={M.reduce ? { opacity: 0 } : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: DUR.base, ease: EASE.out }}>
              {tab === "kits" ? <KitLibrary kits={kits} jobs={jobs} onChange={load} />
                : current ? <InterviewDetail key={current.id} it={current} kits={kits} onReschedule={setRescheduleFor} onDebrief={setDebriefFor} onOffer={setOfferFor} onSaveKit={saveKit} />
                : <EmptyState icon={tab === "debrief" ? ClipboardCheck : CalendarClock} title={EMPTY[tab][0]} description={EMPTY[tab][1]} action={tab === "upcoming" && <Button onClick={() => setShowSchedule(true)} className="mt-2" data-testid="empty-schedule">Schedule interview</Button>} />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {showSchedule && <ScheduleModal cands={cands} kits={kits} jobs={jobs} onClose={() => setShowSchedule(false)} onDone={() => { setShowSchedule(false); load(); }} />}
      {debriefFor && <DebriefModal interview={debriefFor} kits={kits} onClose={() => setDebriefFor(null)} onDone={() => { setDebriefFor(null); load(); }} />}
      {rescheduleFor && <RescheduleModal interview={rescheduleFor} onClose={() => setRescheduleFor(null)} onDone={() => { setRescheduleFor(null); load(); }} />}
      {offerFor && <OfferModal interview={offerFor} onClose={() => setOfferFor(null)} onDone={() => { setOfferFor(null); load(); }} />}
    </div>
  );
}

function KitLibrary({ kits, jobs, onChange }) {
  const [name, setName] = useState("");
  const [jobId, setJobId] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const job = jobs.find((j) => j.id === jobId);
      await api.post("/interview-kits", { name, job_id: jobId || null, role: job?.title || "", generate: true });
      toast.success("Kit generated with AI");
      setName(""); setJobId(""); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate kit"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/interview-kits/${id}`); toast.success("Kit removed"); onChange(); };

  return (
    <div className="space-y-5" data-testid="kit-library">
      <div className="rounded-xl border border-border/60 bg-card p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" /> Build a reusable kit</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Kit name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Backend loop" data-testid="kit-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Base on role</Label>
            <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="kit-job" className="w-full h-10 rounded-md bg-card border border-border px-3 text-sm">
              <option value="">General</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={generate} disabled={busy} data-testid="generate-kit" className="gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate with AI</Button>
      </div>

      {kits.length === 0 ? (
        <EmptyState icon={Library} title="No kits yet" description="Generate your first reusable interview kit above." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="kit-list">
          {kits.map((k) => (
            <div key={k.id} className="rounded-xl border border-border/60 bg-card p-5 space-y-3" data-testid={`kit-${k.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{k.name}</div>
                  {k.role && <div className="text-xs text-muted-foreground">{k.role}</div>}
                </div>
                <button onClick={() => remove(k.id)} data-testid={`delete-kit-${k.id}`} className="text-muted-foreground hover:text-rose-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="space-y-2">
                {(k.competencies || []).map((c, i) => (
                  <div key={i} className="text-sm">
                    <div className="font-medium text-foreground/90">{c.name}</div>
                    <ul className="mt-1 space-y-1">
                      {(c.questions || []).map((q, qi) => <li key={qi} className="text-xs text-muted-foreground flex gap-1.5"><span className="text-primary">•</span> {q}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DateTimePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(value ? new Date(value) : undefined);
  const [time, setTime] = useState(value ? value.slice(11, 16) : "10:00");
  const commit = (d, t) => {
    if (!d) return;
    const p = (n) => String(n).padStart(2, "0");
    onChange(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${t}`);
  };
  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="schedule-date-trigger"
            className="flex-1 min-w-0 h-10 rounded-md bg-card border border-border px-3 text-sm text-left flex items-center gap-2 hover:border-primary/50 transition-colors whitespace-nowrap overflow-hidden">
            <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className={`truncate ${date ? "" : "text-muted-foreground"}`}>{date ? date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "Pick a date"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} initialFocus
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            onSelect={(d) => { setDate(d); commit(d, time); setOpen(false); }} />
        </PopoverContent>
      </Popover>
      <Input type="time" value={time} onChange={(e) => { setTime(e.target.value); commit(date, e.target.value); }} data-testid="schedule-time" className="w-24 shrink-0" />
    </div>
  );
}

function ScheduleModal({ cands, kits, jobs, onClose, onDone }) {
  const [jobId, setJobId] = useState("");
  const [mode2, setMode2] = useState("existing"); // existing | external
  const [candidateId, setCandidateId] = useState("");
  const [extEmail, setExtEmail] = useState("");
  const [extName, setExtName] = useState("");
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState(45);
  const [mode, setMode] = useState("Video");
  const [kitId, setKitId] = useState("");
  const [interviewers, setInterviewers] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Candidates narrowed to the selected role (jobs with none are still schedulable via "external").
  const jobCands = jobId ? cands.filter((c) => c.job_id === jobId) : cands;
  const selectedJob = jobs.find((j) => j.id === jobId);

  // Auto-suggest the role's question kit when the job changes.
  const suggestKit = (jid) => {
    const job = jobs.find((j) => j.id === jid);
    if (!job) return;
    const byJob = kits.find((k) => k.job_id === jid);
    const byRole = kits.find((k) => (k.role || "").trim().toLowerCase() === (job.title || "").trim().toLowerCase());
    const match = byJob || byRole;
    if (match) setKitId(match.id);
  };
  const onJobChange = (jid) => {
    setJobId(jid);
    setCandidateId("");
    suggestKit(jid);
  };

  const submit = async () => {
    if (!when) { toast.error("Pick a date/time"); return; }
    const payload = { when: new Date(when).toISOString(), duration_min: Number(duration), mode, kit_id: kitId || null, notes,
      interviewers: interviewers.split(",").map((s) => s.trim()).filter(Boolean) };
    if (mode2 === "existing") {
      if (!candidateId) { toast.error("Pick a candidate"); return; }
      payload.candidate_id = candidateId;
    } else {
      if (!jobId) { toast.error("Choose the role this interview is for"); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(extEmail.trim())) { toast.error("Enter a valid candidate email"); return; }
      payload.job_id = jobId;
      payload.external_email = extEmail.trim();
      payload.external_name = extName.trim();
    }
    setBusy(true);
    try {
      await api.post("/interviews", payload);
      toast.success("Interview scheduled");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} title="Schedule interview" testId="schedule-modal">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Role</Label>
          <select value={jobId} onChange={(e) => onJobChange(e.target.value)} data-testid="schedule-job" className="w-full h-10 rounded-md bg-card border border-border px-3 text-sm">
            <option value="">All roles</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
        </div>

        <div className="flex gap-2" data-testid="schedule-mode-toggle">
          {[["existing", "Existing candidate"], ["external", "Invite by email"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode2(k)} data-active={mode2 === k} data-testid={`schedule-source-${k}`}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${mode2 === k ? "border-primary text-primary bg-primary/10" : "text-muted-foreground border-border hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        {mode2 === "existing" ? (
          <div className="space-y-1.5">
            <Label>Candidate</Label>
            <select value={candidateId} onChange={(e) => setCandidateId(e.target.value)} data-testid="schedule-candidate" className="w-full h-10 rounded-md bg-card border border-border px-3 text-sm">
              <option value="">Select a candidate…</option>
              {jobCands.map((c) => <option key={c.id} value={c.id}>{`${c.name} — ${c.job_title}`}</option>)}
            </select>
            {jobCands.length === 0 && (
              <p className="text-xs text-amber-400" data-testid="schedule-no-candidates">No candidates in {selectedJob ? `“${selectedJob.title}”` : "your pipeline"} yet — switch to “Invite by email” to interview an external candidate.</p>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="schedule-external">
            <div className="space-y-1.5"><Label>Candidate email</Label><Input type="email" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} placeholder="alex@example.com" data-testid="schedule-ext-email" /></div>
            <div className="space-y-1.5"><Label>Name (optional)</Label><Input value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Alex Doe" data-testid="schedule-ext-name" /></div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Date &amp; time</Label><DateTimePicker value={when} onChange={setWhen} /></div>
          <div className="space-y-1.5"><Label>Duration (min)</Label><Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="schedule-duration" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Mode</Label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="schedule-mode" className="w-full h-10 rounded-md bg-card border border-border px-3 text-sm">
              {["Video", "Onsite", "Phone"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Question kit {kitId && <span className="text-primary text-[11px]">· suggested</span>}</Label>
            <select value={kitId} onChange={(e) => setKitId(e.target.value)} data-testid="schedule-kit" className="w-full h-10 rounded-md bg-card border border-border px-3 text-sm">
              <option value="">None</option>
              {kits.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-1.5"><Label>Interviewers (names or emails, comma separated)</Label><Input value={interviewers} onChange={(e) => setInterviewers(e.target.value)} placeholder="priya@acme.com, Alex" data-testid="schedule-panel" /></div>
        <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="schedule-notes" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="schedule-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule"}</Button>
        </div>
      </div>
    </Overlay>
  );
}

function DebriefModal({ interview, kits, onClose, onDone }) {
  const kit = kits.find((k) => k.id === interview.kit_id);
  const comps = (kit?.competencies || []).map((c) => c.name);
  const [recommendation, setRecommendation] = useState("advance");
  const [ratings, setRatings] = useState(() => comps.map((c) => ({ competency: c, score: 3 })));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const setScore = (i, v) => setRatings((r) => r.map((x, xi) => (xi === i ? { ...x, score: v } : x)));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/interviews/${interview.id}/debrief`, { recommendation, ratings, notes });
      toast.success("Debrief captured — decision logged to audit trail");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save debrief"); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} title={`Debrief · ${interview.candidate_name}`} testId="debrief-modal">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Recommendation</Label>
          <div className="flex gap-2" data-testid="debrief-recommendation">
            {["advance", "hold", "decline"].map((r) => (
              <button key={r} onClick={() => setRecommendation(r)} data-active={recommendation === r} data-testid={`rec-${r}`}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${recommendation === r ? `${REC_COLOR[r]} border-current bg-current/10` : "text-muted-foreground border-border hover:text-foreground"}`}>
                {REC[r]}
              </button>
            ))}
          </div>
        </div>
        {ratings.length > 0 && (
          <div className="space-y-3">
            <Label>Competency ratings (1–5)</Label>
            {ratings.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3" data-testid={`rating-${i}`}>
                <span className="text-sm text-foreground/90 flex-1 truncate">{r.competency}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setScore(i, n)} data-testid={`rating-${i}-${n}`}
                      className={`h-7 w-7 rounded-md text-xs font-mono transition-colors ${r.score >= n ? "bg-primary text-primary-foreground" : "bg-muted/50 text-muted-foreground hover:bg-muted"}`}>{n}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Evidence-based summary…" data-testid="debrief-notes" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="debrief-submit" className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save decision</Button>
        </div>
      </div>
    </Overlay>
  );
}

function RescheduleModal({ interview, onClose, onDone }) {
  const [when, setWhen] = useState(interview.when ? new Date(interview.when).toISOString().slice(0, 16) : "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!when) { toast.error("Pick a new date/time"); return; }
    setBusy(true);
    try {
      await api.patch(`/interviews/${interview.id}`, { when: new Date(when).toISOString() });
      toast.success("Rescheduled — updated invite emailed");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reschedule"); }
    finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} title={`Reschedule · ${interview.candidate_name}`} testId="reschedule-modal">
      <div className="space-y-4">
        <div className="space-y-1.5"><Label>New date &amp; time</Label><DateTimePicker value={when} onChange={setWhen} /></div>
        <p className="text-xs text-muted-foreground">The candidate{interview.interviewers?.length ? " and panel" : ""} will get an updated invite automatically.</p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="reschedule-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reschedule"}</Button>
        </div>
      </div>
    </Overlay>
  );
}

function OfferModal({ interview, onClose, onDone }) {
  const [title, setTitle] = useState(interview.job_title !== "—" ? interview.job_title : "");
  const [compensation, setCompensation] = useState("");
  const [startDate, setStartDate] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Add a role title"); return; }
    setBusy(true);
    try {
      await api.post("/offers", { candidate_id: interview.candidate_id, title, compensation, start_date: startDate, message });
      toast.success(`Offer sent to ${interview.candidate_name}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send offer"); }
    finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} title={`Send offer · ${interview.candidate_name}`} testId="offer-modal">
      <div className="space-y-4">
        <div className="space-y-1.5"><Label>Role title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="offer-title" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Compensation</Label><Input value={compensation} onChange={(e) => setCompensation(e.target.value)} placeholder="$180k + equity" data-testid="offer-comp" /></div>
          <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="offer-start" /></div>
        </div>
        <div className="space-y-1.5"><Label>Personal message</Label><Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="We were impressed by…" data-testid="offer-message" /></div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="offer-submit" className="gap-1.5"><Gift className="h-4 w-4" /> {busy ? "Sending…" : "Send offer"}</Button>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ title, testId, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card dark:bg-[#0d0f14] glass p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-semibold text-lg">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
